import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

/**
 * Smart Reports authentication.
 *
 * Smart Reports is a separate FastAPI service with its own users and its own
 * JWTs. Rather than authenticating against it directly, we ask our own backend
 * to broker a token: `POST /api/accounts/sr-token/` authenticates the caller
 * with their normal InfiniCare JWT and returns a Smart Reports token minted
 * server-side.
 *
 * This replaces an earlier scheme that derived each user's Smart Reports
 * password in the browser (`btoa(email) + '_SR2024!'`). That formula shipped in
 * the public JS bundle, so anyone who knew a user's email address could log in
 * as them and read their blood reports. No credential is derived or held here
 * any more — only the returned token.
 *
 * The exported surface is unchanged, so `smartReportsAxios` and
 * `smartReportsService` need no modification.
 */

const SR_ACCESS_KEY = 'sr_accessToken';
const SR_REFRESH_KEY = 'sr_refreshToken';

interface BrokeredToken {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
  sr_user_id?: string;
}

const storeTokens = (data: BrokeredToken) => {
  if (data.access_token) {
    localStorage.setItem(SR_ACCESS_KEY, data.access_token);
  }
  if (data.refresh_token) {
    localStorage.setItem(SR_REFRESH_KEY, data.refresh_token);
  }
};

/**
 * Ask the backend to mint a Smart Reports token for the current user.
 * `forceRefresh` bypasses the server-side token cache — use it when Smart
 * Reports has just rejected the token we hold.
 */
const brokerToken = async (forceRefresh = false): Promise<boolean> => {
  try {
    const { data } = await axiosInstance.post<BrokeredToken>(
      API_ENDPOINTS.auth.smartReportsToken,
      { force_refresh: forceRefresh }
    );
    if (!data?.access_token) {
      console.error('Smart Reports auth: broker returned no access token');
      return false;
    }
    storeTokens(data);
    return true;
  } catch (err) {
    // A 401 here means the InfiniCare session itself is gone — the main
    // axiosInstance already handles that. Anything else is a Smart Reports
    // availability problem and must not disturb the rest of the app.
    console.error('Smart Reports auth: token broker failed', err);
    return false;
  }
};

/**
 * Ensure a Smart Reports token is present. Cheap when one already is.
 */
export const ensureAuthenticated = async (): Promise<boolean> => {
  const existing = localStorage.getItem(SR_ACCESS_KEY);
  if (existing) return true;
  return brokerToken(false);
};

/**
 * Called by the axios interceptor on 401 — discard the rejected token and ask
 * the broker for a fresh one, bypassing its cache.
 */
export const silentLogin = async (): Promise<void> => {
  localStorage.removeItem(SR_ACCESS_KEY);
  localStorage.removeItem(SR_REFRESH_KEY);

  const success = await brokerToken(true);
  if (!success) {
    throw new Error('Smart Reports session could not be renewed');
  }
};

/**
 * Clear Smart Reports tokens (call on InfiniCare logout).
 */
export const clearSRAuth = () => {
  localStorage.removeItem(SR_ACCESS_KEY);
  localStorage.removeItem(SR_REFRESH_KEY);
};

const smartReportsAuthService = {
  ensureAuthenticated,
  silentLogin,
  clearSRAuth,
};

export default smartReportsAuthService;
